// alert-message.component.ts
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertType, SectionType } from '@core/enums';
import { IconComponent } from '@shared/components/ui';
import { GetAlertState, GetRoles, GetUserId, SaveAlertState } from '@core/utils';
import { AlertStateType } from '@core/types';

@Component({
  selector: 'app-alert-message',
  imports: [CommonModule, IconComponent, TranslateModule],
  templateUrl: './alert-message.component.html',
  styleUrls: ['./alert-message.component.scss'],
})
export class AlertMessageComponent implements OnInit, OnChanges {
  /**
   * The main alert message to display.
   * @example 'Your profile has been updated successfully.'
   */
  @Input() message: string;
  /**
   * Additional detailed description for the alert message.
   * @example 'Changes will be reflected in your account within 24 hours.'
   */
  @Input() detailMessage: string;
  /**
   * Reason provided when a request is denied or rejected.
   * @example 'Your request was rejected due to missing information.'
   */
  @Input() denyReason: string;
  /**
   * Icon name to display in the alert. Default is 'notice'.
   * @example 'error' | 'success' | 'notice' | 'warning'
   * @default 'notice'
   */
  @Input() iconName = 'notice';
  /**
   * Color of the icon. Default is white.
   * @example 'var(--primary)' | '#FF0000' | 'var(--white)'
   * @default 'var(--white)'
   */
  @Input() iconColor = 'var(--white)';
  /**
   * CSS class that determines the alert's style. Affects background color and overall appearance.
   * @example 'success' for successful operations, 'error' for errors
   * @default 'warning'
   */
  @Input() cssClass: 'success' | 'info' | 'warning' | 'error' = 'warning';
  /**
   * The type of alert, from AlertType enum. Determines behavior and appearance.
   * @example AlertType.SUCCESS | AlertType.ERROR
   */
  @Input() alertType: AlertType | null;
  /**
   * Whether to show a close icon to dismiss the alert. Default is true.
   * @example false to create a persistent alert that can't be dismissed
   * @default true
   */
  @Input() showCloseIcon = true;
  /**
   * Custom top margin for the alert component.
   * @example '10px' | '1rem' | '2em'
   */
  @Input() marginTop: string;
  /**
   * Custom bottom margin for the alert component.
   * @example '10px' | '1rem' | '2em'
   */
  @Input() marginBottom: string;
  /**
   * Size variant of the alert. Affects padding and font sizes.
   * @example 'small' for compact alerts, 'large' for prominent alerts
   * @default 'medium'
   */
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  // When storeCloseState is true, sectionType must be provided.
  /**
   * Whether to store the closed state of the alert in local storage. Default is false.
   * @example true to remember the closed state across sessions
   * @default false
   */
  @Input() storeCloseState = false;
  /**
   * The section type of the alert, from SectionType enum. Required when storeCloseState is true.
   * @example SectionType.PROFILE | SectionType.BANK_DETAILS
   */
  @Input() sectionType: SectionType;
  /**
   * The entity ID associated with the alert. Optional and used for specific sections.
   * @Optional
   * @example 12345 | 67890
   */
  @Input() entityId?: number;
  // Controls whether the alert is rendered in the template.
  displayAlert = true;
  private readonly _translateService = inject(TranslateService);

  ngOnInit() {
    if (this.storeCloseState) {
      if (!this.sectionType) {
        throw new Error('sectionType is required when storeCloseState is true');
      }
      this._checkStoredAlertState();
    }
    if (this.displayAlert) {
      this.setAlertProperties();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Re-check stored state when alertType changes.
    if (changes['alertType'] && !changes['alertType'].firstChange && this.storeCloseState) {
      this._checkStoredAlertState();
    }
    if (this.displayAlert) {
      this.setAlertProperties();
    }
  }

  /**
   * Set alert display properties based on the alertType.
   */
  setAlertProperties() {
    switch (this.alertType) {
      case AlertType.SUCCESS:
        this.cssClass = 'success';
        break;
      case AlertType.INFO:
        this.cssClass = 'info';
        break;
      case AlertType.WARNING:
        this.cssClass = 'warning';
        break;
      case AlertType.ERROR:
        this.cssClass = 'error';
        break;
      case AlertType.PENDING_REQUEST:
        this.message = this._translateService.instant('Current Request is waiting for the Approval by Admin');
        this.cssClass = 'warning';
        break;
      case AlertType.REJECTED_REQUEST:
        this.message = this._translateService.instant('Your current request is Rejected');
        this.cssClass = 'error';
        break;
      case AlertType.APPROVED_REQUEST:
        this.message = this._translateService.instant('Your current request is Approved');
        this.cssClass = 'success';
        break;
      case AlertType.WAITING_ADMIN_APPROVAL:
        this.message = this._translateService.instant('Current Request is waiting for the Approval by Admin');
        this.cssClass = 'warning';
        break;
      case AlertType.UPDATE_WAITING_ADMIN_APPROVAL: {
        const msg = this._isUserAdmin() ? 'Update request is waiting for the approval' : 'Your update request is waiting for the approval by Admin';
        this.message = this._translateService.instant(msg);
        this.cssClass = 'info';
        break;
      }
      case AlertType.DENY_BY_ADMIN: {
        const msg = this._isUserAdmin() ? 'Current request has been denied' : 'Your current request is Denied by Admin';
        this.message = this._translateService.instant(msg);
        this.cssClass = 'error';
        break;
      }
      default:
        break;
    }
  }

  /**
   * Close the alert, update UI, and save its closed state.
   */
  closeAlert() {
    this.displayAlert = false;
    this.message = '';
    if (this.storeCloseState) {
      const userId = +GetUserId();
      const alertState: AlertStateType = {
        userId,
        alertType: this.alertType,
        sectionType: this.sectionType,
        entityId: this.entityId,
        closed: true,
        timestamp: Date.now(),
      };
      SaveAlertState(userId, this.sectionType, this.alertType, alertState, this.entityId);
    }
  }

  /**
   * Check the stored alert state to decide whether to display the alert.
   */
  private _checkStoredAlertState() {
    const userId = GetUserId();
    const storedState = GetAlertState(userId, this.sectionType, this.entityId);
    // Only display the alert if no closed state exists for this alert type.
    if (storedState && storedState.closed && storedState.alertType === this.alertType) {
      this.displayAlert = false;
    } else {
      this.displayAlert = true;
    }
  }

  private _isUserAdmin(): boolean {
    const roles = GetRoles();
    return roles.includes('admin') || roles.includes('Admin');
  }
}
